import getConfig from 'next/config';

import { AD_DETAILS_DFP_CONFIG } from '../../../../../configs/dfpConfig';
import { theme } from '../../../../../helpers/Theme';

import { Link } from '../../../../../components/Toolkit/Button/Link';
import { ShareAd } from '../../../../../components/Toolkit/ShareAd/ShareAd';
import { WhitePanel } from '../../../../../components/Toolkit/WhitePanel/WhitePanel';
import { DFPUnit } from '../../../../../components/Toolkit/DFP/DFP';

import * as Styled from '../../../../../components/PageWrappers/AdDetails/components/AdTitleBox/AdTitleBox.styled';
import { useAdTitleBox } from '../../../../../components/PageWrappers/AdDetails/components/AdTitleBox/AdTitleBox.hook';
import { AdTitleBoxProps } from '../../../../../components/PageWrappers/AdDetails/components/AdTitleBox/AdTitleBox.typed';
import { MetaList } from '../../../../../components/Toolkit/InfoTitle/MetaList/MetaList';
import { Tag } from '../../../../../components/Toolkit/Tags/Tag';
import { FEATURE_FREE_HISTORY_CHECK } from '../../../../../utils';
import { REPORT_TYPES } from '../../../../../api/types/greenlightTypes';
import { useConvertContext } from '../../../../../contexts/ConvertContext';
import { PAGE } from '../../../../../helpers/pages';
import { TrustIndicatorTags } from '../../../../../components/PageWrappers/SharedComponents/TrustIndicatorTags/TrustIndicatorTags';
import { PricingIndicatorIconText } from '../../../../../components/PricingIndicator/PricingIndicatorIconText/PricingIndicatorIconText';
import { Text } from '../../../../ToolkitV2/Text/Text.styled';

const {
  publicRuntimeConfig: { FINANCE_PROVIDERS_DFP_SLOTS_ENABLED },
} = getConfig();

const AdTitleBox = (props: AdTitleBoxProps) => {
  const {
    title,
    listItems,
    currency,
    price,
    oldPrice,
    renderSaveAd,
    className,
    isViewingOwnAd = false,
    handleShareAd,
    deliveryAvailable = false,
    adId,
    renderBoostButton,
    renderEditButton,
    manufacturerApprovedBadge,
    specialOffer,
    meritsV2,
    greenlightVerified,
    greenlightV2,
    cardPayments,
    financeInfo,
    financeLink,
    wanted,
    carFinance,
    adAnalytics = true,
    priceOnRequest = false,
    isSold = false,
    priceIndicator,
  } = props;

  const {
    publicRuntimeConfig: { FEATURE_PRICING_INDICATOR },
  } = getConfig();

  const { showPricingIndicator } = useConvertContext({ page: PAGE.AD_DETAILS });

  const freeHistoryReportTag =
    FEATURE_FREE_HISTORY_CHECK &&
    greenlightV2?.reportInfo?.reportType === REPORT_TYPES.FREE_REPORT;

  const {
    handleAnalyticsView,
    hasFinanceLink,
    finance,
    handleFinanceLinkClick,
    financeClassImp,
    priceText,
    isPriceOnRequest,
    handlePPMClick,
  } = useAdTitleBox({
    adId,
    carFinance,
    currency,
    financeInfo,
    financeLink,
    price,
    priceOnRequest,
    title,
  });

  const getFinanceLinkContent = () => {
    if (financeInfo && financeInfo.partnerName === 'bankofirelandjournal') {
      return <DFPUnit {...AD_DETAILS_DFP_CONFIG.dd_detail_textlink} />;
    } else if (hasFinanceLink) {
      return (
        <Link
          data-tracking="get-finance-approval-link"
          href={finance.url}
          target="_blank"
          rel="noopener nofollow"
          colorVariant="BLUE"
        >
          {finance.text}
        </Link>
      );
    } else {
      return <DFPUnit {...AD_DETAILS_DFP_CONFIG.dd_detail_textlink_alt} />;
    }
  };

  return (
    <WhitePanel className={className} testId="title-box">
      <Styled.TitleAndUserActionsContainer>
        <Styled.Header>
          <Styled.AdTitle
            as="h1"
            title={'title'}
            shouldTruncate={false}
            variant="DEFAULT"
            flip={false}
          >
            {title}
          </Styled.AdTitle>
          {listItems && <MetaList listItems={listItems} variant="SMALL" />}
        </Styled.Header>
        {isViewingOwnAd && (
          <Styled.OwnAdButtonContainer renderBoostButton={renderBoostButton}>
            {renderBoostButton}
            {renderEditButton}
          </Styled.OwnAdButtonContainer>
        )}
      </Styled.TitleAndUserActionsContainer>

      <TrustIndicatorTags
        deliveryAvailable={deliveryAvailable}
        cardPayments={cardPayments}
        meritsV2={meritsV2}
        specialOffer={specialOffer}
        wanted={wanted}
        greenlightVerified={greenlightVerified}
        manufacturerApprovedBadge={manufacturerApprovedBadge}
        freeHistoryReportTag={freeHistoryReportTag}
      />

      {isSold && (
        <Styled.SoldTag>
          <Tag text={'Sold'} bgColor={'RED_DARK'} />
        </Styled.SoldTag>
      )}

      <Styled.PriceAndActionsContainer>
        <Styled.PriceAndDeliveryContainer>
          {oldPrice && (
            <div>
              <Styled.OldPrice>
                {currency === 'EUR' ? '€' : '£'}
                {oldPrice}
              </Styled.OldPrice>
            </div>
          )}

          <Styled.PriceContainer>
            <Styled.PriceRow>
              <Styled.Price isPriceOnRequest={isPriceOnRequest}>
                {priceText}
              </Styled.Price>

              {showPricingIndicator &&
                FEATURE_PRICING_INDICATOR &&
                priceIndicator?.state && (
                  <PricingIndicatorIconText
                    status={priceIndicator.state}
                    textSize="M14"
                    // This is feature flagged off on prod
                    onClick={() => {
                      // TODO: Trigger modal opening
                      console.log('Open modal');
                    }}
                  />
                )}
            </Styled.PriceRow>

            {financeInfo && (
              <Styled.PricePerMonthButton
                ofType="TERTIARY"
                onClick={handlePPMClick}
              >
                <Text size="M14" color="GREY_DARK" weight="regular">
                  {`From €${financeInfo.pricePerMonth.priceDisplay}/mo`}
                </Text>
              </Styled.PricePerMonthButton>
            )}
          </Styled.PriceContainer>
        </Styled.PriceAndDeliveryContainer>

        <Styled.Actions isViewingOwnAd={isViewingOwnAd && !adAnalytics}>
          <Styled.PositionedActions>
            {isViewingOwnAd && adAnalytics ? (
              <Styled.AnalyticsContainer
                onClick={() => (title ? handleAnalyticsView() : null)}
              >
                <Styled.AnalyticsButton size="SMALL">
                  {/* TODO: update to bar chart icon */}
                  <Styled.BarChartIc color={theme.colors.BLUE} />
                  <Styled.AnalyticsTextContainer>
                    View Analytics
                  </Styled.AnalyticsTextContainer>
                </Styled.AnalyticsButton>
              </Styled.AnalyticsContainer>
            ) : null}
            <Styled.ShareAdContainer isViewingOwnAd={isViewingOwnAd}>
              <ShareAd handleShareAd={handleShareAd} iconSizeDesktop="SM" />
            </Styled.ShareAdContainer>
            {/*  Extra boolean test included to account for Storybook controls: */}
            {!isViewingOwnAd ? renderSaveAd : null}
          </Styled.PositionedActions>
        </Styled.Actions>
      </Styled.PriceAndActionsContainer>

      {/* Legacy Ad unit behaviour */}
      {FINANCE_PROVIDERS_DFP_SLOTS_ENABLED !== 'true' &&
        (financeInfo && financeInfo.partnerName === 'bankofirelandjournal' ? (
          <Styled.FinanceLinkWrapper
            onClick={handleFinanceLinkClick}
            className={carFinance?.banner ? financeClassImp : ''}
          >
            <DFPUnit {...AD_DETAILS_DFP_CONFIG.dd_detail_textlink} />
          </Styled.FinanceLinkWrapper>
        ) : (
          hasFinanceLink && (
            <Styled.FinanceLinkWrapper
              onClick={handleFinanceLinkClick}
              className={carFinance?.banner ? financeClassImp : ''}
            >
              <Link
                data-tracking="get-finance-approval-link"
                href={finance.url}
                target="_blank"
                rel="noopener nofollow"
              >
                {finance.text}
              </Link>
            </Styled.FinanceLinkWrapper>
          )
        ))}

      {FINANCE_PROVIDERS_DFP_SLOTS_ENABLED === 'true' && (
        <Styled.FinanceLinkWrapper
          onClick={handleFinanceLinkClick}
          className={carFinance?.banner ? financeClassImp : ''}
        >
          {getFinanceLinkContent()}
        </Styled.FinanceLinkWrapper>
      )}
    </WhitePanel>
  );
};

export { AdTitleBox };
